'use client'

// The Pino cam: his world, live, for streaming.
//
// On with ?cam=pino. Three views, V cycles them live (or &view=...):
//   drone     (default) a drone chasing him: behind and above, on a
//             spring, banking into his turns, with the free drone's wind
//             wobble, climbing over a building rather than through it
//   eyes      just past the tip of his beak, looking where he looks
//   shoulder  close behind and above him
//
// Sam, 29 Sep: it "should feel more like the motion of the drone". The
// free drone (DroneCamera.jsx) feels alive because of three things, and
// the chase view borrows all three: motion that eases in and out instead
// of tracking him rigidly (a spring, not a lerp), a bank toward the turn,
// and a multi-frequency wobble so it is wind and not a rail.
//
// Reads the bird exactly as drawn (lib/world/birdPose.js), so the camera
// and the bird on screen never disagree about where he is.

import { useEffect, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { birdPose } from '../../lib/world/birdPose'
import useGameStore from '../../stores/gameStore'
import { useObstacles } from './obstacles/ObstacleProvider'

// Measured off bird.glb at instance scale 1: the beak runs from z 1.45
// to 1.84 with its tip at y 1.43. The eyes view sits just past the tip so
// the beak never fills the frame.
const VIEWS = {
  drone: { forward: -6.5, up: 4.2, aimUp: 1.1, aimAhead: 2.5, fov: 58, spring: 5, turn: 1.3, bank: true, wind: 1 },
  eyes: { forward: 1.95, up: 1.5, aimUp: 0.6, aimAhead: 10, fov: 72, spring: 14, turn: 2.2, bank: false, wind: 0.25 },
  shoulder: { forward: -4.4, up: 3.2, aimUp: 1.3, aimAhead: 5, fov: 62, spring: 8, turn: 1.8, bank: true, wind: 0.6 },
}
const ORDER = ['drone', 'eyes', 'shoulder']
const JUMP = 14             // further than this from where it should be: cut, do not fly
const BANK_MAX = 0.3        // ~17 degrees at the hardest turn
const BANK_PER_TURN = 0.35  // roll per radian a second of turning
const BANK_RATE = 3.4       // the drone's bank in and out
// The free drone's wind, same numbers (DroneCamera.jsx).
const WIND_POS = 0.07
const WIND_ROT = 0.011
const CLEARANCE = 0.8       // how far outside a building the drone keeps
const CLIMB_MAX = 8

const wrap = (a) => {
  while (a > Math.PI) a -= Math.PI * 2
  while (a < -Math.PI) a += Math.PI * 2
  return a
}

function inside(x, y, z, obstacles) {
  for (let i = 0; i < obstacles.length; i++) {
    const b = obstacles[i]?.box
    if (!b) continue
    if (x > b.min.x - CLEARANCE && x < b.max.x + CLEARANCE && z > b.min.z - CLEARANCE && z < b.max.z + CLEARANCE
      && y > b.min.y - CLEARANCE && y < b.max.y + CLEARANCE) return true
  }
  return false
}

// One step of a critically damped spring pulling `pos` toward `to`.
function spring(pos, vel, to, rate, h, scratch) {
  vel.addScaledVector(scratch.copy(to).sub(pos), rate * rate * h).addScaledVector(vel, -2 * rate * h)
  pos.addScaledVector(vel, h)
}

export function pinoCamRequested() {
  if (typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get('cam') === 'pino'
}

export function PinoCam() {
  const { camera } = useThree()
  const { obstacles } = useObstacles()
  const view = useRef((() => {
    if (typeof window === 'undefined') return 'drone'
    const v = new URLSearchParams(window.location.search).get('view')
    return ORDER.includes(v) ? v : 'drone'
  })())
  const s = useRef({
    yaw: null, pos: new THREE.Vector3(), vel: new THREE.Vector3(), aim: new THREE.Vector3(), aimVel: new THREE.Vector3(),
    roll: 0, t: 0, fresh: true,
  })
  const target = useRef(new THREE.Vector3())
  const aimTarget = useRef(new THREE.Vector3())
  // Scratch, so a stream that runs for days is not allocating every frame.
  const pull = useRef(new THREE.Vector3())

  // The page hides its buttons and panels and draws the stream overlay.
  useEffect(() => {
    useGameStore.getState().setPinoCam(view.current)
    const onKey = (e) => {
      if (e.code !== 'KeyV' || /INPUT|TEXTAREA|SELECT/.test(e.target?.tagName || '')) return
      view.current = ORDER[(ORDER.indexOf(view.current) + 1) % ORDER.length]
      s.current.fresh = true // re-seat, no sweep across the town
      useGameStore.getState().setPinoCam(view.current)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      useGameStore.getState().setPinoCam(null)
    }
  }, [])

  useFrame((_, dtRaw) => {
    if (!birdPose.seen) return
    const dt = Math.min(0.1, dtRaw)
    const v = VIEWS[view.current]
    const st = s.current
    if (camera.fov !== v.fov) {
      camera.fov = v.fov
      camera.updateProjectionMatrix()
    }
    const asleep = birdPose.action === 'SLEEP'

    // His heading, lagged: the camera swings round after him rather than
    // being bolted to his snaps.
    const lastYaw = st.yaw
    if (st.yaw == null || st.fresh) st.yaw = birdPose.rotY
    else if (!asleep) st.yaw = wrap(st.yaw + wrap(birdPose.rotY - st.yaw) * (1 - Math.exp(-dt * v.turn)))
    const yawRate = lastYaw == null || st.fresh ? 0 : wrap(st.yaw - lastYaw) / Math.max(dt, 1e-3)
    const fx = Math.sin(st.yaw)
    const fz = Math.cos(st.yaw)

    // Where it wants to be, and where it wants to look.
    target.current.set(birdPose.x + fx * v.forward, birdPose.y + v.up, birdPose.z + fz * v.forward)
    // Something in the way: close in on him first, the way a chase drone
    // does, and only climb over it when even close is inside something.
    // Climbing first put it on top of a tree looking straight down.
    if (v.forward < 0 && obstacles?.length) {
      let back = v.forward
      while (back < -2.2 && inside(birdPose.x + fx * back, target.current.y, birdPose.z + fz * back, obstacles)) back += 0.5
      target.current.x = birdPose.x + fx * back
      target.current.z = birdPose.z + fz * back
      let climb = 0
      while (climb < CLIMB_MAX && inside(target.current.x, target.current.y + climb, target.current.z, obstacles)) climb += 0.5
      target.current.y += climb
    }
    aimTarget.current.set(birdPose.x + fx * v.aimAhead, birdPose.y + v.aimUp, birdPose.z + fz * v.aimAhead)
    if (view.current === 'eyes') aimTarget.current.y = target.current.y - 0.9

    if (st.fresh || st.pos.distanceTo(target.current) > JUMP) {
      st.pos.copy(target.current)
      st.aim.copy(aimTarget.current)
      st.vel.set(0, 0, 0)
      st.aimVel.set(0, 0, 0)
      st.roll = 0
      st.fresh = false
    } else {
      // A critically damped spring: it eases off and eases in, which is
      // the difference between flying and being dragged. Stepped at
      // 120Hz whatever the frame rate, or a stiff spring on a slow frame
      // overshoots and throws the camera across the town.
      const steps = Math.max(1, Math.ceil(dt * 120))
      const h = dt / steps
      for (let i = 0; i < steps; i++) {
        spring(st.pos, st.vel, target.current, v.spring, h, pull.current)
        spring(st.aim, st.aimVel, aimTarget.current, v.spring * 1.4, h, pull.current)
      }
    }

    // Bank toward the turn, and level out after it.
    const wantRoll = v.bank ? Math.max(-BANK_MAX, Math.min(BANK_MAX, -yawRate * BANK_PER_TURN)) : 0
    st.roll += (wantRoll - st.roll) * Math.min(1, dt * BANK_RATE)

    // Wind: the free drone's wobble, two octaves each way.
    st.t += dt
    const t = st.t
    const w = v.wind
    const wx = (Math.sin(t * 0.70 + 0.50) * 0.6 + Math.sin(t * 1.90 + 2.30) * 0.3) * WIND_POS * w
    const wy = (Math.sin(t * 0.60 + 1.40) * 0.6 + Math.sin(t * 2.30 + 4.10) * 0.3) * WIND_POS * 0.55 * w
    const wz = (Math.sin(t * 0.85 + 3.20) * 0.6 + Math.sin(t * 1.70 + 0.80) * 0.3) * WIND_POS * w
    const rp = (Math.sin(t * 1.20 + 0.30) * 0.6 + Math.sin(t * 2.70 + 1.70) * 0.3) * WIND_ROT * w
    const ry = (Math.sin(t * 0.90 + 1.10) * 0.5 + Math.sin(t * 2.10 + 3.40) * 0.3) * WIND_ROT * w
    const rr = (Math.sin(t * 1.40 + 2.20) * 0.7 + Math.sin(t * 3.10 + 0.60) * 0.3) * WIND_ROT * 1.5 * w

    camera.position.set(st.pos.x + wx, st.pos.y + wy, st.pos.z + wz)
    camera.lookAt(st.aim)
    camera.rotateZ(st.roll + rr)
    camera.rotateX(rp)
    camera.rotateY(ry)
  })

  return null
}

export default PinoCam
